import { type CSSProperties, type HTMLAttributes } from 'react';
import {
  getMachineStatusIconColor,
  getMachineStatusIconDefinition,
  getMachineStatusIconProgressStep,
  getMachineStatusIconVariant,
  getMachineStatusIconViewBox,
  type MachineStatusIconMachineType,
  type MachineStatusIconStatus,
} from './machine-status-icon.shared';

// half-diagonal of the X drawn over the drum for the notAvailable variant
const CROSS_RADIUS = 54;

type MachineStatusIconProps = HTMLAttributes<HTMLSpanElement> & {
  machineType: MachineStatusIconMachineType;
  status: MachineStatusIconStatus;
  progressStep?: number | null;
  title?: string;
  // overrides the status color
  color?: string;
};

export function MachineStatusIcon({
  machineType,
  status,
  progressStep,
  className,
  style,
  title,
  color: colorOverride,
  ...props
}: MachineStatusIconProps) {
  const definition = getMachineStatusIconDefinition(machineType, status);
  const variant = getMachineStatusIconVariant(status);
  const color = colorOverride ?? getMachineStatusIconColor(status);
  const filledSegments = getMachineStatusIconProgressStep(progressStep);
  const rotatingPathIndexes = new Set(definition.drumRotation?.pathIndexes ?? []);
  const viewBox = getMachineStatusIconViewBox(definition);
  const segmentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    aspectRatio: `${viewBox.width} / ${viewBox.height}`,
    ...style,
  } satisfies CSSProperties;

  return (
    <span className={className} style={segmentStyle} {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        width="100%"
        height="100%"
        aria-hidden={title ? undefined : true}
        role={title ? 'img' : undefined}
      >
        {title ? <title>{title}</title> : null}
        {definition.paths.map((path, index) =>
          rotatingPathIndexes.has(index) ? null : <path key={index} d={path} fill={color} />,
        )}

        {definition.drumRotation ? (
          <g>
            {variant === 'running' ? (
              <animateTransform
                attributeName="transform"
                type="rotate"
                from={`0 ${definition.drumRotation.cx} ${definition.drumRotation.cy}`}
                to={`360 ${definition.drumRotation.cx} ${definition.drumRotation.cy}`}
                dur={`${definition.drumRotation.durationMs ?? 1600}ms`}
                repeatCount="indefinite"
              />
            ) : null}
            {definition.drumRotation.pathIndexes.map(index => (
              <path key={index} d={definition.paths[index]!} fill={color} />
            ))}
          </g>
        ) : null}

        {variant === 'notAvailable' && definition.drumRotation ? (
          <g strokeLinecap="round" transform={`translate(${definition.drumRotation.cx} ${definition.drumRotation.cy})`}>
            {/* white underlay separates the X from the drum artwork */}
            <line
              x1={-CROSS_RADIUS}
              y1={-CROSS_RADIUS}
              x2={CROSS_RADIUS}
              y2={CROSS_RADIUS}
              stroke="#ffffff"
              strokeWidth={22}
            />
            <line
              x1={-CROSS_RADIUS}
              y1={CROSS_RADIUS}
              x2={CROSS_RADIUS}
              y2={-CROSS_RADIUS}
              stroke="#ffffff"
              strokeWidth={22}
            />
            <line
              x1={-CROSS_RADIUS}
              y1={-CROSS_RADIUS}
              x2={CROSS_RADIUS}
              y2={CROSS_RADIUS}
              stroke={color}
              strokeWidth={11}
            />
            <line
              x1={-CROSS_RADIUS}
              y1={CROSS_RADIUS}
              x2={CROSS_RADIUS}
              y2={-CROSS_RADIUS}
              stroke={color}
              strokeWidth={11}
            />
          </g>
        ) : null}

        {variant === 'running' && definition.progress ? (
          <>
            {Array.from({ length: 4 }).map((_, index) => {
              const segmentWidth = (definition.progress!.width - definition.progress!.gap * 3) / 4;

              return (
                <rect
                  key={index}
                  x={definition.progress!.x + index * (segmentWidth + definition.progress!.gap)}
                  y={definition.progress!.y}
                  width={segmentWidth}
                  height={definition.progress!.height}
                  rx={definition.progress!.radius}
                  fill="#ffffff"
                  opacity={index < filledSegments ? 0.98 : 0.24}
                />
              );
            })}
          </>
        ) : null}
      </svg>
    </span>
  );
}
